import React, { useEffect, useState } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { PendingProduct } from '../../../@types/Ecommerce';
import { useDispatch, useSelector } from 'react-redux';
import {
	ANALYTICS_EVENTS,
	EVENT_TYPES,
} from '../../../shared/constants/events';
import useEvents from '../../../hooks/useEvents';
import { RootState } from '../../../redux/store';
import {
	getOrderRefresh,
	getPendingOrderDetail,
} from '../../../services/sales.service';
import { Spinner } from '@/components/ui/spinner';
import ROUTES from '../../../shared/constants/routes';
import { useNavigate } from 'react-router-dom';
import HomeCard from '../../../components/molecules/HomeCard';
import Button from '../../../components/atoms/Button';
import ModalReference from './ModalReference';
import PendingCard from './PendingCard';
import { setPaymentInstructions } from '../../../redux/slices/cart.slice';
import { PAYMENT_METHOD } from '../../../@types/Checkout.type';
import {
	OrderRefreshMapper,
	ProductVersionItem,
	TUserOrder,
} from '../../../@types/Order.type';
import { useProductLookup } from '../../../hooks/useProductLookup';

interface LastPendingOrderProps {
	isSingle?: boolean;
}

const LastPendingOrder: React.FC<LastPendingOrderProps> = ({
	isSingle = false,
}) => {
	const userProfile = useSelector((state: RootState) => state.profile.user);
	const { tokenSession } = useSelector((state: RootState) => state.auth);
	const lookup = useProductLookup();

	const [lastPendingOrder, setLastPendingOrder] = useState<PendingProduct>();
	const [allPendingOrders, setAllPendingOrders] = useState<
		OrderRefreshMapper[]
	>([]);
	const [isLoadingPendingOrders, setIsLoadingPendingOrders] = useState(false);
	const [lastOrder, setLastOrder] = useState<TUserOrder>();
	const dispatch = useDispatch();
	const { trackEvent } = useEvents();

	const getPendingOrder = async () => {
		try {
			setIsLoadingPendingOrders(true);
			const params =
				'?status=pending&is_renewal=false&shopping_cart_treatment=NORMAL';
			const orders = await getOrderRefresh(params, tokenSession);

			if (orders) {
				setAllPendingOrders(orders);
				const lastOrder = orders.sort((a: any, b: any) => b.id - a.id)?.[0];

				// El refresh del engine responde snake_case (shopping_item /
				// product_version_id); aceptar ambos shapes — sin esto el .map
				// truena y el catch esconde la card.
				const rawItems =
					lastOrder?.shopping_item ?? lastOrder?.shoppingItem ?? [];
				const items: ProductVersionItem[] = rawItems.map(
					(item: any) => ({
						...item,
						productVersionId: item.product_version_id ?? item.productVersionId,
						additionalInformation:
							item.additional_information ?? item.additionalInformation,
						productVersion: {
							// Only `name` is rendered; rest kept as neutral defaults.
							product: {
								id: 0,
								name:
									lookup.get(item.product_version_id ?? item.productVersionId)
										?.name ?? 'Producto desconocido',
								defaultProductVersionId:
									item.product_version_id ?? item.productVersionId,
								description: '',
								isPurchasable: false,
								benefits: [],
								slug: '',
								category: '',
								regime: [],
							},
							periodicity: 0,
							price: item.total ?? 0,
						},
					}),
				);

				const newOrder: TUserOrder = {
					...lastOrder,
					shoppingItem: items,
				};
				setLastOrder(newOrder);
				if (newOrder) {
					const order = await getPendingOrderDetail(newOrder?.id, tokenSession);
					if (order && order.payment_method_name !== 'Card') {
						setLastPendingOrder(order);
					}
				}
			}
		} catch (error: any) {
		} finally {
			setIsLoadingPendingOrders(false);
		}
	};

	useEffect(() => {
		if (!userProfile || lookup.size === 0) return;
		getPendingOrder();
	}, [userProfile, lookup]);

	const logReferencePaymentEvent = (
		action: 'copy_payment_number' | 'finalize_purchase',
	) => {
		if (userProfile) {
			trackEvent({
				eventName: ANALYTICS_EVENTS.HOME_LINK,
				eventPayload: {
					event_type: EVENT_TYPES.HOME_PENDING_PURCHASE_INTERACTION,
					id: userProfile?.id,
					phone: userProfile?.cellphone,
					action,
				},
			});
		}
	};

	const onOpenInstructions = () => {
		logReferencePaymentEvent('finalize_purchase');
		if (!lastPendingOrder || !lastOrder) return;
		dispatch(
			setPaymentInstructions({
				paymentMethod:
					lastPendingOrder?.payment_method_name === PAYMENT_METHOD.SPEI
						? PAYMENT_METHOD.SPEI
						: PAYMENT_METHOD.CASH,
				show: true,
				total: lastPendingOrder.total,
				reference: lastPendingOrder.payment_reference_code ?? '',
				referenceLink: lastPendingOrder.payment_method_reference_link ?? '',
				expiresAt: lastPendingOrder.expires_at ?? 0,
			}),
		);
	};

	const [showModalPaymentSteps, setShowModalPaymentSteps] = useState(false);

	const navigate = useNavigate();

	return (
		<>
			{allPendingOrders?.length > 0 &&
				lastPendingOrder &&
				lastPendingOrder?.payment_reference_code && (
					<>
						<ModalReference
							paymentMethod={lastPendingOrder?.payment_method_name ?? ''}
							showModal={showModalPaymentSteps}
							setShowModal={setShowModalPaymentSteps}
							reference={lastPendingOrder?.payment_reference_code ?? ''}
							total={lastPendingOrder?.total ?? 0}
							expiresAt={lastPendingOrder?.expires_at}
							referenceLink={lastPendingOrder?.payment_method_reference_link}
						/>
						<HomeCard
							title={`${
								allPendingOrders.length > 1
									? 'Pagos pendientes'
									: 'Pago pendiente'
							}`}
							showButton={false}
							headerButton={
								<>
									{allPendingOrders.length > 1 && (
										<Button
											onClick={() => {
												navigate(ROUTES.PENDING_REFERENCES);
											}}
											type='button'
											label={'Ver todas mis referencias'}
											variant='text'
											icon={<ArrowRight size={20} />}
											labelColor='primary'
										/>
									)}
								</>
							}
						>
							{isLoadingPendingOrders || lookup.size === 0 || !userProfile ? (
								<div className='flex items-center justify-center'>
									<Spinner className="size-6" />
								</div>
							) : (
								<>
									<PendingCard
										accountNumber={
											lastPendingOrder?.payment_reference_code ?? ''
										}
										paymentMethodName={
											lastPendingOrder?.payment_method_name ?? ''
										}
										dateLimit={lastPendingOrder?.expires_at ?? ''}
										total={lastPendingOrder?.total ?? 0}
										items={lastOrder?.shoppingItem ?? []}
										onPress={onOpenInstructions}
										isSingle={isSingle}
									/>
								</>
							)}
						</HomeCard>
					</>
				)}
		</>
	);
};

export default React.memo(LastPendingOrder);
